import { Card } from '@/design-system/card/card';
import { ErrorSection } from '@/design-system/error-section/error-section';
import { Inline } from '@/design-system/inline/inline';
import { Select } from '@/design-system/select/select';
import { Stack } from '@/design-system/stack/stack';
import { SupportedCountryCurrency } from '@/types/supported-country-currency';
import { cn } from '@/utils/cn';
import { exhaustiveCheck } from '@/utils/exhaustive-check';
import { getAmountWithCurrency } from '@/utils/get-amount-with-currency';
import * as Sentry from '@sentry/react';
import { ComponentProps, useState } from 'react';
import { getReturnColorClass } from '../../utils/get-return-color-class';
import { ReturnData } from '../../utils/get-return-data';
import { PeriodSelectionContext } from './period-selection-context/period-selection-context';
import { ReturnsBreakdown } from './returns-breakdown';
import { ReturnsChartWrapper } from './returns-chart-wrapper';

type SelectedPeriod = string | null;

const variants = ['MONTH', 'YEAR', 'ALL_TIME'] as const;
export type ReturnCardVariant = (typeof variants)[number];

type ReturnsDrawerContentProps = {
  currency: SupportedCountryCurrency;
  returnData: ReturnData;
};

export const ReturnsDrawerContent = ({ currency, returnData }: ReturnsDrawerContentProps) => {
  const firstMonthlyReturnPeriod = returnData.monthlyReturns[0].period;
  const firstYearlyReturnPeriod = returnData.yearlyReturns[0].period;

  const [selectedVariant, setSelectedVariant] = useState<ReturnCardVariant>('MONTH');
  const [selectedPeriod, setSelectedPeriod] = useState<SelectedPeriod>(firstMonthlyReturnPeriod);

  const cardContent = getCardContent({
    currency,
    returnData,
    selectedVariant,
    selectedPeriod,
  });

  const onSelectPeriod = (period: string) => {
    setSelectedPeriod(period);
  };

  const onVariantChange = (value: ReturnCardVariant) => {
    switch (value) {
      case 'MONTH':
        setSelectedPeriod(firstMonthlyReturnPeriod);

        return;
      case 'YEAR':
        setSelectedPeriod(firstYearlyReturnPeriod);

        return;
      case 'ALL_TIME':
        setSelectedPeriod(null);

        return;
      default:
        return exhaustiveCheck(value);
    }
  };

  if (!cardContent) {
    return <ErrorSection />;
  }

  const {
    returnLabel,
    formattedGrossReturnAmount,
    feeLabel,
    formattedFeesAmount,
    formattedNetReturnAmount,
    valueColorClass,
  } = cardContent;

  return (
    <PeriodSelectionContext.Provider
      value={{ onSelectPeriod, resetToFirstPeriod: () => onVariantChange(selectedVariant) }}
    >
      <Stack space={6}>
        <Card className="px-6 py-4">
          <Stack space={6}>
            <Inline space={4} justify="between" wrap="nowrap" className="w-full">
              <Stack space={1}>
                <p className="text-xs font-normal text-nowrap text-muted-foreground">{returnLabel}</p>

                <p className={cn('text-xl font-medium text-foreground', valueColorClass)}>{formattedNetReturnAmount}</p>
              </Stack>

              <div className="w-[150px]">
                <Select
                  placeholder="Select"
                  value={selectedVariant}
                  onValueChange={(value: ReturnCardVariant) => {
                    setSelectedVariant(value);

                    onVariantChange(value);
                  }}
                >
                  {variants.map((variant) => {
                    const optionLabel = getOptionLabel(variant);

                    return (
                      <Select.Item key={variant} value={variant}>
                        {optionLabel}
                      </Select.Item>
                    );
                  })}
                </Select>
              </div>
            </Inline>

            {selectedVariant !== 'ALL_TIME' && (
              <ReturnsChartWrapper variant={selectedVariant} returnData={returnData} />
            )}
          </Stack>
        </Card>

        <ReturnsBreakdown
          returnLabel={returnLabel}
          grossReturn={formattedGrossReturnAmount}
          feeLabel={feeLabel}
          feesAmount={formattedFeesAmount}
          netReturn={formattedNetReturnAmount}
        />
      </Stack>
    </PeriodSelectionContext.Provider>
  );
};

type GetCardContentProps = ComponentProps<typeof ReturnsDrawerContent> & {
  selectedVariant: ReturnCardVariant;
  selectedPeriod: SelectedPeriod;
};

const getCardContent = ({ currency, returnData, selectedVariant, selectedPeriod }: GetCardContentProps) => {
  switch (selectedVariant) {
    case 'MONTH': {
      const selectedMonthlyReturn = returnData.monthlyReturns.find(
        (monthlyReturn) => monthlyReturn.period === selectedPeriod,
      );

      if (!selectedMonthlyReturn) {
        Sentry.captureMessage('Selected period is not valid in returns-drawer-content, MONTH variant', {
          extra: {
            currency,
            returnData,
            selectedVariant,
            selectedPeriod,
          },
        });

        return;
      }

      const netReturnAmount = selectedMonthlyReturn.totalReturn;
      const feesAmount = selectedMonthlyReturn.totalFees;
      const grossReturnAmount = netReturnAmount + feesAmount;

      return {
        returnLabel: `Returns in ${selectedMonthlyReturn.label}`,
        formattedGrossReturnAmount: getAmountWithCurrency({
          amount: grossReturnAmount,
          currency,
          isAccountBalance: true,
        }),
        feeLabel: `Assets fee in ${selectedMonthlyReturn.label}`,
        formattedFeesAmount: getAmountWithCurrency({
          amount: feesAmount * -1,
          currency,
          isAccountBalance: true,
        }),
        formattedNetReturnAmount: getAmountWithCurrency({
          amount: netReturnAmount,
          currency,
          isAccountBalance: true,
        }),
        valueColorClass: getReturnColorClass(netReturnAmount),
      };
    }
    case 'YEAR': {
      const selectedYearlyReturn = returnData.yearlyReturns.find(
        (yearlyReturn) => yearlyReturn.period === selectedPeriod,
      );

      if (!selectedYearlyReturn) {
        Sentry.captureMessage('Selected period is not valid in returns-drawer-content, YEAR variant', {
          extra: {
            currency,
            returnData,
            selectedVariant,
            selectedPeriod,
          },
        });

        return;
      }

      const netReturnAmount = selectedYearlyReturn.totalReturn;
      const feesAmount = selectedYearlyReturn.totalFees;
      const grossReturnAmount = netReturnAmount + feesAmount;

      return {
        returnLabel: `Returns in ${selectedYearlyReturn.period}`,
        formattedGrossReturnAmount: getAmountWithCurrency({
          amount: grossReturnAmount,
          currency,
          isAccountBalance: true,
        }),
        feeLabel: `Assets fee in ${selectedYearlyReturn.period}`,
        formattedFeesAmount: getAmountWithCurrency({
          amount: feesAmount * -1,
          currency,
          isAccountBalance: true,
        }),
        formattedNetReturnAmount: getAmountWithCurrency({
          amount: netReturnAmount,
          currency,
          isAccountBalance: true,
        }),
        valueColorClass: getReturnColorClass(netReturnAmount),
      };
    }
    case 'ALL_TIME': {
      const netReturnAmount = returnData.allTimeReturns;
      const feesAmount = returnData.allTimeFees;
      const grossReturnAmount = netReturnAmount + feesAmount;

      return {
        returnLabel: 'All time returns',
        formattedGrossReturnAmount: getAmountWithCurrency({
          amount: grossReturnAmount,
          currency,
          isAccountBalance: true,
        }),
        feeLabel: 'All time assets fees',
        formattedFeesAmount: getAmountWithCurrency({
          amount: feesAmount * -1,
          currency,
          isAccountBalance: true,
        }),
        formattedNetReturnAmount: getAmountWithCurrency({
          amount: netReturnAmount,
          currency,
          isAccountBalance: true,
        }),
        valueColorClass: getReturnColorClass(netReturnAmount),
      };
    }
    default:
      return exhaustiveCheck(selectedVariant);
  }
};

const getOptionLabel = (variant: ReturnCardVariant) => {
  switch (variant) {
    case 'MONTH':
      return 'Monthly';
    case 'YEAR':
      return 'Yearly';
    case 'ALL_TIME':
      return 'All Time';
    default:
      return exhaustiveCheck(variant);
  }
};
